/**
 * @typedef {Object} InterceptPayload The data that is provided in various places of the interception process
 * @property {string} endpoint The REST endpoint that is/was used for the request
 * @property {boolean} morph Whether the element should be/was morphed instead of just replaced
 * @property {boolean} viewTransition Whether browser-native View Transitions should be/hav been used
 * @property {boolean} fixScrollPosition Whether scroll position correction has been requested
 * @property {number} page The page number that was navigated to
 * @property {Record<string, string>} queryParams The query parameters that should be/were applied
 */

/**
 * @param {import('alpinejs').default} Alpine
 */
export default Alpine => {
  /**
   * x-intercept directive
   *
   * Intercepts form GET submissions and link clicks from inside the element which
   * either change just the query parameters or the /page/* part of the URL.
   * All other navigations are not intercepted.
   *
   * Options:
   * - endpoint:          The REST endpoint (without the /wp-json/ prefix)
   *                      to fetch the new content from.
   *                      May be a string or a function that returns a string
   *                      and receives an InterceptPayload object.
   *                      The REST endpoint is expected to return a JSON object with
   *                      the new HTML on the 'output' property.
   * - nonce:             The nonce to use for the REST request. If not provided, no nonce is used.
   * - morph:             Whether to morph the element (with the @alpinejs/morph plugin)
   *                      instead of just replacing its HTML
   *                      Default: true
   * - viewTransition:    Whether to use browser-native View Transitions if available
   *                      Default: true
   * - fixScrollPosition: Whether to correct the scroll position after the content replacement.
   *                      This uses a simple heuristic to ensure that the upper or lower edge
   *                      of the element (depending on which is nearer at the time of the
   *                      interception) remains at the same position in the viewport.
   *                      Default: true
   *
   * The directive emits two events on the element that attempted to navigate:
   * - interceptor:before: Dispatched before the interception happens.
   *                       The event.detail is an InterceptPayload object.
   *                       The event is cancelable. If the event is canceled,
   *                       the interception is not performed.
   * - interceptor:after:  Dispatched after the interception happened.
   *                       The event.detail is an InterceptPayload object.
   *
   * You can manually trigger the replacement by dispatching an 'update' event. Optionally,
   * an object with 'page' and/or 'queryParams' properties can be passed as the event.detail, e.g.
   * $dispatch('update', { page: 2 })
   */
  Alpine.directive('intercept', async (element, directive, utilities) => {
    // We only load the interceptor if the page has a replaceable element
    // since the interceptor has a non-trivial amount of code and
    // is not needed immediately after page initialization
    const { runInterceptor } = await import('./interceptor-lib.js')
    runInterceptor(element, directive, utilities)
  })
}
